import { useSearchStore } from './store'
import { FILTER_TAGS, STATIC_FILTER_TAGS } from './constants'
import type { RecentSearch, RentalFilters, SavedSearch } from './types'

/**
 * Load a saved / recent search into the search store from ANYWHERE in the app.
 *
 * The established pattern (app/searches/page.tsx, components/auth/user-menu.tsx,
 * components/search/saved-searches-panel.tsx) is "write the zustand store, then
 * router.push('/search')" — the search page's mount effect picks the state up
 * and runs the query. These helpers are that logic, callable outside React so
 * the global command palette can use it too.
 *
 * Those three existing call sites still carry their own copies; they could
 * adopt this helper in a later cleanup pass.
 */

/**
 * Fired when the store was updated while the user is ALREADY on /search — a
 * router.push to the route you are on does not remount, so nothing would
 * re-run. app/search/page.tsx listens and re-runs the query.
 */
export const RERUN_SEARCH_EVENT = 'roostr:rerun-search'

export function requestSearchRerun() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(RERUN_SEARCH_EVENT))
}

/** Mirrors handleOpenInSearch in app/searches/page.tsx. */
export function applySavedSearch(search: SavedSearch) {
  const s = useSearchStore.getState()

  s.setQuery(search.original_query || '')
  s.setFilters((search.parsed_filters as RentalFilters) || ({ country: 'CZ' } as RentalFilters))
  s.setSort('newest')
  s.setTravelTime(search.travel_time || null)
  s.clearTags()

  // Static tags map to numeric listing fields and are applied client-side, so
  // they must not be re-added as keyword tags.
  const staticTagIds = new Set(STATIC_FILTER_TAGS.map((tag) => tag.id))
  if (search.tags && search.tags.length > 0) {
    search.tags.forEach((tagId) => {
      if (!staticTagIds.has(tagId)) s.addTag(tagId)
    })
  } else if (search.parsed_filters) {
    // Older saved searches predate `tags` — reconstruct them from the filters.
    FILTER_TAGS.forEach((tag) => {
      if (staticTagIds.has(tag.id)) return
      const filterValue = search.parsed_filters?.[tag.value.field]
      if (filterValue !== undefined && filterValue === tag.value.value) s.addTag(tag.id)
    })
  }
}

/** Mirrors handleSelectRecentSearch in components/search/search-input.tsx. */
export function applyRecentSearch(recent: RecentSearch) {
  const s = useSearchStore.getState()

  if (recent.type === 'text' && recent.query) {
    s.setQuery(recent.query)
    s.clearFilters()
  } else {
    s.setQuery('')
    if (recent.filters) s.setFilters({ ...recent.filters })
    s.clearTags()
    recent.tags?.forEach((tag) => s.addTag(tag))
  }

  if (recent.city) s.setSelectedCity(recent.city)
  if (recent.districts) s.setSelectedDistricts(recent.districts)
  if (recent.travelTime !== undefined) s.setTravelTime(recent.travelTime)
  if (recent.sort) s.setSort(recent.sort)
}
